import moment from 'moment';
import { date, pitchType, pitchSubType, pitchTypeDisplayName, pitchTypeColor,
    pitchSubTypeColor, hitType, percent, number, baseEvent, pitchResult, dateTranslate } from 'yankees-react-utils';

function mapPPFXReport(translate, criteria, resp, bySeason = false, startYear = null) {
    const allPitches = resp?.pitches?.filter((p) => p.pitchtype > 1) || [];
    const countseries = {
        all: mapPPFXCountSeries(translate, allPitches, criteria),
        early: mapPPFXCountSeries(translate, allPitches.filter((x) => x.balls < 2 && x.strikes < 2), criteria),
        firstpitch: mapPPFXCountSeries(translate, allPitches?.filter((x) => x.isfirstpitch), criteria),
        count11: mapPPFXCountSeries(translate, allPitches?.filter((x) => x.balls === 1 && x.strikes === 1), criteria),
        count2k: mapPPFXCountSeries(translate, allPitches?.filter((x) => x.strikes === 2 && x.balls !== 3), criteria),
        count32: mapPPFXCountSeries(translate, allPitches?.filter((x) => x.balls === 3 && x.strikes === 2), criteria),
        behind: mapPPFXCountSeries(translate, allPitches?.filter((x) => x.balls > x.strikes && x.balls !== 3 && x.strikes !== 2), criteria),
        hitterahead: mapPPFXCountSeries(translate, allPitches?.filter((x) => x.balls > x.strikes && !(x.balls === 3 && x.strikes === 2)), criteria),
    };

    const playerName = (resp != null && resp.playerstrikes != null && resp.playerstrikes[0]) ? `${resp.playerstrikes[0].usesname} ${resp.playerstrikes[0].lastname}` : '';
    const yearID = (resp != null && resp.seasonline != null && resp.seasonline[0] ? resp.seasonline[0].yearid : new Date().getFullYear());
    const inningBreaks = getInningPlotBands(allPitches);
    const pitchtypeseries = {
        pitchloc: {},
        velo: { inningBreaks },
        breakx: { inningBreaks },
        breakz: { inningBreaks },
        releasex: { inningBreaks },
        releasez: { inningBreaks },
        angle: { inningBreaks },
        spinrate: { inningBreaks },
        extension: { inningBreaks },
        pitchinjuryrisk: {},
        gyrodegree: { inningBreaks },
        spindirection: { inningBreaks },
    };
    for (let i = 0; i < resp?.pitchtypes?.length || 0; i += 1) {
        const type = resp.pitchtypes[i];
        const pitches = allPitches?.filter((x) => x.pitchtype === type.pitchtype && x.fxsubtype === type.fxsubtype);
        const trendpoints = resp.trendpoints.filter((x) => x.pitchtype === type.pitchtype && x.fxsubtype === type.fxsubtype);

        pitchtypeseries.pitchloc[type.fxsubtype || type.pitchtype] = {
            lhb: plotPitchLocationsByResult(translate, pitches.filter((x) => x.islhb === true), playerName),
            rhb: plotPitchLocationsByResult(translate, pitches.filter((x) => x.islhb === false), playerName),
        };
        if (pitchtypeseries.pitchloc[type.fxsubtype || type.pitchtype].lhb) {
            pitchtypeseries.pitchloc[type.fxsubtype || type.pitchtype].lhb.attack = plotPitchLocationsByResult(translate, pitches.filter((x) => x.islhb && x.strikes < 2));
            pitchtypeseries.pitchloc[type.fxsubtype || type.pitchtype].lhb.twoStrikes = plotPitchLocationsByResult(translate, pitches.filter((x) => x.islhb && x.strikes === 2));
        }
        if (pitchtypeseries.pitchloc[type.fxsubtype || type.pitchtype].rhb) {
            pitchtypeseries.pitchloc[type.fxsubtype || type.pitchtype].rhb.attack = plotPitchLocationsByResult(translate, pitches.filter((x) => !x.islhb && x.strikes < 2));
            pitchtypeseries.pitchloc[type.fxsubtype || type.pitchtype].rhb.twoStrikes = plotPitchLocationsByResult(translate, pitches.filter((x) => !x.islhb && x.strikes === 2));
        }

        if (type.pitchtype > 1) {
            // assign arm angle property
            pitches.forEach((x) => {
                x.armangle = Math.round(calculateReleaseAngle(x.initposx, x.initposz, x.height) * 100) / 100;
            });
            trendpoints.forEach((x) => {
                x.armangle = Math.round(calculateReleaseAngle(x.initposx, x.initposz, allPitches[0]?.height ?? x.height) * 100) / 100;
            });

            pitchtypeseries.velo[type.fxsubtype || type.pitchtype] = bySeason
                ? plotPitchValueSeason('initspeed54', translate('data.velocity'), pitches, trendpoints)
                : plotPitchValue(translate, 'initspeed54', translate('data.velocity'), pitches, trendpoints, yearID, inningBreaks);
            pitchtypeseries.breakx[type.fxsubtype || type.pitchtype] = bySeason
                ? plotPitchValueSeason('breakx54', translate('pitcher-overview.pitch-break.horiz-break'), pitches, trendpoints)
                : plotPitchValue(translate, 'breakx54', translate('pitcher-overview.pitch-break.horiz-break'), pitches, trendpoints, yearID, inningBreaks);
            pitchtypeseries.breakz[type.fxsubtype || type.pitchtype] = bySeason
                ? plotPitchValueSeason('breakz54', translate('pitcher-overview.pitch-break.vert-break'), pitches, trendpoints)
                : plotPitchValue(translate, 'breakz54', translate('pitcher-overview.pitch-break.vert-break'), pitches, trendpoints, yearID, inningBreaks);
            pitchtypeseries.releasex[type.fxsubtype || type.pitchtype] = bySeason
                ? plotPitchValueSeason('initposx', translate('data.release-point-side'), pitches, trendpoints)
                : plotPitchValue(translate, 'initposx', translate('data.release-point-side'), pitches, trendpoints, yearID, inningBreaks);
            pitchtypeseries.releasez[type.fxsubtype || type.pitchtype] = bySeason
                ? plotPitchValueSeason('initposz', translate('data.release-point-height'), pitches, trendpoints)
                : plotPitchValue(translate, 'initposz', translate('data.release-point-height'), pitches, trendpoints, yearID, inningBreaks);
            pitchtypeseries.angle[type.fxsubtype || type.pitchtype] = bySeason
                ? plotPitchValueSeason('armangle', translate('data.arm-angle'), pitches, trendpoints)
                : plotPitchValue(translate, 'armangle', translate('data.arm-angle'), pitches, trendpoints, yearID, inningBreaks);
            pitchtypeseries.spinrate[type.fxsubtype || type.pitchtype] = bySeason
                ? plotPitchValueSeason('spinrate', translate('data.spin-rate'), pitches, trendpoints)
                : plotPitchValue(translate, 'spinrate', translate('data.spin-rate'), pitches, trendpoints, yearID, inningBreaks);
            pitchtypeseries.extension[type.fxsubtype || type.pitchtype] = bySeason
                ? plotPitchValueSeason('extension', translate('data.extension'), pitches, trendpoints)
                : plotPitchValue(translate, 'extension', translate('data.extension'), pitches, trendpoints, yearID, inningBreaks);
            pitchtypeseries.gyrodegree[type.fxsubtype || type.pitchtype] = bySeason
                ? plotPitchValueSeason('gyrodegree', 'Gyro Degree', pitches, trendpoints)
                : plotPitchValue(translate, 'gyrodegree', 'Gyro Degree', pitches, trendpoints, yearID, inningBreaks);
            pitchtypeseries.spindirection[type.fxsubtype || type.pitchtype] = bySeason
                ? plotPitchValueSeason('spindirection', 'Spin Direction', pitches, trendpoints)
                : plotPitchValue(translate, 'spindirection', 'Spin Direction', pitches, trendpoints, yearID, inningBreaks);
        } else {
            pitchtypeseries.pitchinjuryrisk[type.fxsubtype || type.pitchtype] = bySeason
                ? plotPitchValueSeason('pitchinjuryrisk', translate('data.injury-risk'), pitches, trendpoints)
                : plotPitchValue(translate, 'pitchinjuryrisk', translate('data.injury-risk'), pitches, trendpoints);
        }
    }

    const pitches = allPitches?.map((p) => ({
        ...p,
        tooltip: `
            ${translate('baseball.break-x')}: ${translate('format.abbr-inches', { value: number(p.breakx54, 1) })}<br />
            ${translate('baseball.break-z')}: ${translate('format.abbr-inches', { value: number(p.breakz54, 1) })}<br />
            ${translate('data.release-x')}: ${translate('format.abbr-feet', { value: number(p.initposx, 1) })}<br />
            ${translate('data.release-z')}: ${translate('format.abbr-feet', { value: number(p.initposz, 1) })}<br />
            ${p.istop ? translate('baseball.abbr-top') : translate('baseball.abbr-bottom')} ${p.inning}, ${p.outs} ${translate('baseball.outs_other')}, ${p.balls}-${p.strikes}
        `,
    }));

    return {
        countseries,
        pitchtypeseries,
        vsteams: resp?.vsteams,
        gamestats: resp?.gamestats,
        gameline: resp?.gameline,
        seasonline: resp?.seasonline,
        seasonstats: resp?.seasonstats?.filter((x) => startYear === null || x.yearid >= startYear),
        pitchtypes: resp?.pitchtypes,
        pitches,
        seasonstdev: resp?.seasonstdev,
        pitchtypestdev: resp?.pitchtypestdev,
        bybatterhand: resp?.bybatterhand,
        splits: resp?.splits,
    };
}

function mapPPFXCountSeries(translate, pitches, criteria) {
    const pitchLocationsForCount = { lhb: [], rhb: [] };
    if (!pitches || !pitches.length) return pitchLocationsForCount;

    let numLhbPitches = 0; let numRhbPitches = 0;
    for (let i = 0; i < pitches.length; i += 1) {
        if (pitches[i].platex != null && pitches[i].platez != null && pitches[i].pitchtype > 1) {
            let gamedate = '';
            if (criteria.startdate != null && criteria.enddate != null && criteria.startdate != criteria.enddate) gamedate = `${translate('common.date')}: ${date(pitches[i].gamedate)}<br/>`;
            const { pitchtype } = pitches[i];
            const fxsubtype = pitches[i].fxsubtype || 0;
            const point = {
                x: pitches[i].platex,
                y: pitches[i].platez,
                name: '',
                tooltip: `${gamedate
                        + (pitches[i].istop ? `${translate('baseball.abbr-top')} ` : `${translate('baseball.abbr-bottom')} `) + pitches[i].inning}, ${
                    pitches[i].outs} ${translate('baseball.outs_one')}, ${
                    pitches[i].balls}-${pitches[i].strikes}`,
                url: pitches[i].url,
                gameid: pitches[i].gameid,
                eventseq: pitches[i].eventseq,
                pitchseq: pitches[i].pitchseq,
                playid: pitches[i].playid,
                isinstrikezone: pitches[i].isinstrikezone,
                pstrike: pitches[i].pstrike,
                marker: pitches[i].stk ? { symbol: 'circle', radius: 4 } : { symbol: 'square', radius: 4 },
            };

            if (pitches[i].islhb) {
                if (!pitchLocationsForCount.lhb[(1000 * pitchtype) + fxsubtype]) {
                    pitchLocationsForCount.lhb[(1000 * pitchtype) + fxsubtype] = {
                        data: [],
                        name: `${pitchType(pitchtype, true, undefined, translate)}${fxsubtype > 0 ? ` (${pitchSubType(fxsubtype, true, undefined, translate)})` : ''}`,
                        index: (pitchtype * 1000) + (fxsubtype || 0),
                        id: `pitchtype${pitchtype}-${fxsubtype}`,
                        marker: { symbol: 'circle', radius: 4 },
                        color: pitchSubTypeColor({ pitchtype, fxsubtype }),
                    };
                }

                pitchLocationsForCount.lhb[(1000 * pitchtype) + fxsubtype].data.push(point);
                numLhbPitches += 1;
            } else {
                if (!pitchLocationsForCount.rhb[(1000 * pitchtype) + fxsubtype]) {
                    pitchLocationsForCount.rhb[(1000 * pitchtype) + fxsubtype] = {
                        data: [],
                        name: `${pitchType(pitchtype, true, undefined, translate)}${fxsubtype > 0 ? ` (${pitchSubType(fxsubtype, true, undefined, translate)})` : ''}`,
                        index: (pitchtype * 1000) + (fxsubtype || 0),
                        id: `pitchtype${pitchtype}-${fxsubtype}`,
                        marker: { symbol: 'circle', radius: 4 },
                        color: pitchSubTypeColor({ pitchtype, fxsubtype }),
                    };
                }

                pitchLocationsForCount.rhb[(1000 * pitchtype) + fxsubtype].data.push(point);
                numRhbPitches += 1;
            }
        }
    }

    pitchLocationsForCount.lhb = pitchLocationsForCount.lhb.map((x) => {
        if (x && x.name) x.name = `${x.name} (${percent(pitches.filter((z) => ((1000 * z.pitchtype) + z.fxsubtype || 0) === x.index && z.islhb).length / numLhbPitches, 0)})`;
        return x;
    }).filter((x) => x != null);

    if (pitchLocationsForCount.lhb.length === 0) pitchLocationsForCount.lhb = null;

    pitchLocationsForCount.rhb = pitchLocationsForCount.rhb.map((x) => {
        if (x && x.name) x.name = `${x.name} (${percent(pitches.filter((z) => ((1000 * z.pitchtype) + z.fxsubtype || 0) === x.index && !z.islhb).length / numRhbPitches, 0)})`;
        return x;
    }).filter((x) => x != null);

    if (pitchLocationsForCount.rhb.length === 0) pitchLocationsForCount.rhb = null;

    return pitchLocationsForCount;
}

/**
    Generate series by different pitch/AB results, exactly like old ppfx report
*/
function plotPitchLocationsByResult(translate, p, playerName) {
    let series = [
        { data: [], name: translate('data.hard-hit'), index: 1, id: 'hit', color: '#0000ff', marker: { symbol: 'diamond', radius: 4, lineColor: '#0000ff', fillColor: '#ffffff', lineWidth: 2 } },
        { data: [], name: translate('data.hard-out'), index: 2, id: 'out', color: '#0000ff', marker: { symbol: 'triangle', radius: 5 } },
        { data: [], name: translate('baseball.balls_one'), index: 3, id: 'ball', color: '#00ff00', marker: { symbol: 'circle', radius: 5 } },
        { data: [], name: translate('data.called-strike'), index: 4, id: 'stk', color: '#ff0000', marker: { symbol: 'circle', radius: 5 } },
        { data: [], name: translate('data.swinging-strike'), index: 5, id: 'swing', color: '#ff0000', marker: { symbol: 'diamond', radius: 5 } },
        { data: [], name: translate('data.foul'), index: 6, id: 'foul', color: '#ff0000', marker: { symbol: 'square', radius: 4, lineColor: '#ff0000', fillColor: '#ffffff', lineWidth: 2 } },
        { data: [], name: translate('data.weak-hit'), index: 7, id: 'wkhit', color: '#ffa500', marker: { symbol: 'diamond', radius: 4, lineColor: '#ffa500', fillColor: '#ffffff', lineWidth: 2 } },
        { data: [], name: translate('data.weak-out'), index: 8, id: 'wkout', color: '#ffa500', marker: { symbol: 'triangle', radius: 5 } },
    ];

    for (let i = 0; i < p.length; i += 1) {
        if (p[i].platex && p[i].platez) {
            let gamedate = '';
            gamedate += `${translate('common.date')}: ${dateTranslate(translate, p[i].gamedate)}<br/>`;
            const result = p[i].pitchresult;
            const event = p[i].baseevent;
            const point = {
                x: p[i].platex,
                y: p[i].platez,
                name: '',
                tooltip:
                    `${gamedate
                    + (p[i].initspeed54 ? `${translate('format.abbr-mph', { value: number(p[i].initspeed54, 1) })}<br/>` : '')
                    + (p[i].istop ? `${translate('baseball.abbr-top')} ` : `${translate('baseball.abbr-bottom')} `) + p[i].inning}, ${
                        p[i].outs} ${translate('baseball.outs_one')}, ${
                        p[i].balls}-${p[i].strikes
                    }<br />${translate('baseball.batter')}: ${p[i].batter_usesname} ${p[i].batter_lastname
                    }${p[i].islastpitch && (result == 14 || result == 15) ? `<br />${hitType(p[i].hittype)} ${baseEvent(event)}${p[i].exitvelo ? `, ${translate('data.exit-velo')} ${number(p[i].exitvelo, 1)}` : ''}` : ''}`,
                url: p[i].url,
                gameid: p[i].gameid,
                eventseq: p[i].eventseq,
                pitchseq: p[i].pitchseq,
                playid: p[i].playid,
                batter: `${p[i].batter_usesname} ${p[i].batter_lastname}`,
                pitcher: playerName,
                gamedate: p[i].gamedate,
                istop: p[i].istop,
                inning: p[i].inning,
                balls: p[i].balls,
                strikes: p[i].strikes,
            };

            Object.keys(p[i]).forEach((key) => {
                if (key.indexOf('url') >= 0 || key.indexOf('angle') >= 0) point[key] = p[i][key];
            });

            if ((result === 14 || result === 15) && p[i].wellhit && event > 0 && event < 5) {
                series[0].data.push(point);
            } else if ((result === 14 || result === 15) && p[i].wellhit && (event === 8 || (event > 10 && event < 16))) {
                series[1].data.push(point);
            } else if (result === 1) {
                series[2].data.push(point);
            } else if (result === 4) {
                series[3].data.push(point);
            } else if (result === 6) {
                series[4].data.push(point);
            } else if (result === 8) {
                series[5].data.push(point);
            } else if ((result === 14 || result === 15) && !p[i].wellhit && event > 0 && event < 5) {
                series[6].data.push(point);
            } else if ((result === 14 || result === 15) && !p[i].wellhit && (event === 8 || (event > 10 && event < 16))) {
                series[7].data.push(point);
            }
        }
    }

    series = series.filter((x) => x.data.length > 0);
    if (!series.length) return null;
    return series;
}

/**
    Generate scatterplot for the set of pitches with Y-value set by property named in "prop",
    and spline's for the set of trendpoints by yearid.
*/
function plotPitchValueSeason(prop, title, pitches, trendpoints) {
    if ((!pitches || !pitches.length) && (!trendpoints || !trendpoints.length)) {
        return null;
    }

    const series = [
        { data: [], type: 'scatter', turboThreshold: 0, name: title, index: 1, id: prop, showInLegend: false },
    ];
    const gameDates = [...new Set(pitches.map((p) => p.gamedate))];
    gameDates.forEach((g) => {
        const gameDate = date(moment(g));
        const gamePitches = pitches.filter((p) => p.gamedate === g);
        const gameid = gamePitches[0]?.gameid;
        const yValue = gamePitches.reduce((sum, x) => sum + x[prop], 0) / gamePitches.length;
        const point = {
            x: new Date(g),
            y: yValue,
            name: '',
            tooltip: gameDate,
            url: null,
            gameid,
            eventseq: null,
            pitchseq: null,
            playid: null,
            pitchtype: null,
        };
        series[0].data.push(point);
    });

    return series;
}

function plotPitchValue(translate, prop, title, pitches, trendpoints, yearID, inningBreaks = []) {
    if ((!pitches || !pitches.length) && (!trendpoints || !trendpoints.length)) {
        return null;
    }

    let series = [
        { data: [], type: 'scatter', turboThreshold: 0, name: title, index: 1, id: prop, showInLegend: false },
        { data: [], color: '#ff0000', type: 'spline', name: translate('data.game-s'), index: 2, id: 'game' },
    ];
    if (yearID) {
        series = [
            ...series,
            { data: [], color: '#0000ff', type: 'spline', name: yearID, index: 2, id: 'year' },
            { data: [], color: '#00ff00', type: 'spline', name: (yearID - 1), index: 3, id: 'pyear' },
        ];
    }
    if (inningBreaks.length > 0) series.inningBreaks = inningBreaks;

    let maxpitchtotal = 0;

    for (let i = 0; i < pitches.length; i += 1) {
        if (typeof pitches[i][prop] !== 'undefined' && pitches[i][prop] !== null) {
            maxpitchtotal = Math.max(pitches[i].pitchtotal, maxpitchtotal);
            const gamedate = '';
            const point = {
                x: pitches[i].pitchtotal,
                y: pitches[i][prop],
                name: '',
                tooltip: `${gamedate}${translate('data.pitch')} ${pitches[i].pitchtotal}<br/>${
                    pitches[i].istop ? `${translate('baseball.abbr-top')} ` : `${translate('baseball.abbr-bottom')} `}${pitches[i].inning}, ${
                    pitches[i].outs} ${translate('baseball.outs_one')}, ${
                    pitches[i].balls}-${pitches[i].strikes
                }${prop === 'pitchinjuryrisk' ? `<br/>${translate('format.abbr-mph', { value: pitches[i].initspeed })}${pitches[i].lowvelocity ? ` (${translate('data.below-expected')})` : ''}<br/>${pitches[i].extension}' ${translate('data.extension')}${pitches[i].lowextension ? ` (${translate('data.below-expected')})` : ''}` : ''}`,
                url: pitches[i].url,
                gameid: pitches[i].gameid,
                eventseq: pitches[i].eventseq,
                pitchseq: pitches[i].pitchseq,
                playid: pitches[i].playid,
            };

            series[0].data.push(point);
        }
    }

    if (trendpoints) {
        for (let i = 0; i < trendpoints.length; i += 1) {
            const tp = trendpoints[i];
            if (typeof tp[prop] !== 'undefined' && tp[prop] !== null /* && (!maxpitchtotal || (tp.pitchnumber <= maxpitchtotal)) */) {
                const point = {
                    x: tp.pitchnumber,
                    y: tp[prop],
                    name: null,
                    tooltip: null,
                };

                if (tp.yearid === 0) {
                    series[1].data.push(point);
                } else if (tp.yearid === yearID) {
                    series[2].data.push(point);
                } else if (tp.yearid === yearID - 1) {
                    series[3].data.push(point);
                }
            }
        }
    }

    return series;
}

function calculateReleaseAngle(initposx, initposz, height) {
    if (!initposx || initposx == 0) return null;
    return 180.0 * Math.atan((initposz - 0.7 * height / 12.0) / Math.abs(initposx)) / Math.PI;
}

function mapHeatMapsFromParams(heatmaps) {
    const objs = Array.isArray(heatmaps) ? heatmaps : [heatmaps];
    const mappedObjs = objs.map((heatmap) => {
        const params = heatmap.Params.split('|').reduce((acc, p) => {
            const [key, value] = p.split(':');
            const valAsInt = Number(value);
            acc[key] = isNaN(valAsInt) ? value : valAsInt;
            return acc;
        }, {});
        return { ...heatmap, ...params };
    });
    return Array.isArray(heatmaps) ? mappedObjs : mappedObjs[0];
}

function getInningPlotBands(pitches) {
    let prevPitch = 1;
    const innings = new Set([...pitches.map((p) => p.inning)]);
    const inningBreakPlotBands = [];
    innings.forEach((inning, index) => {
        const lastPitch = pitches.filter((p) => p.inning === inning).sort((a, b) => b.pitchtotal - a.pitchtotal)[0];
        inningBreakPlotBands.push({
            color: index % 2 === 1 ? '#E5F4FF' : '#FFF',
            from: prevPitch,
            to: lastPitch.pitchtotal,
            label: {
                text: inning,
            },
        });
        prevPitch = lastPitch.pitchtotal;
    });
    return inningBreakPlotBands;
}

function toSwingDecisionSeries(translate, pitches, showGameScore) {
    return pitches.map((p, idx) => {
        let lineColor;
        let fillColor;
        if (showGameScore) {
            if (p.totalgrade >= 7.5) {
                lineColor = '#008A00';
                fillColor = '#008A00';
            } else if (p.totalgrade >= 5) {
                lineColor = '#90EE90';
                fillColor = '#90EE90';
            } else {
                lineColor = '#000000';
                fillColor = '#ffffff';
            }
        } else {
            lineColor = '#000000';
            fillColor = '#ffffff';
        }
        return {
            name: `&nbsp;<b>${translate('data.pitch')} #${p.abpitchseq}</b>`,
            index: idx + 1,
            id: `${p.eventseq}_${p.pitchseq}`,
            visible: !Number.isNaN(p.score),
            marker: {
                symbol: p.istake ? 'circle' : 'diamond',
                radius: 5,
                lineColor,
                fillColor,
                lineWidth: 2,
            },
            data: [{
                x: p.platex,
                y: p.platez,
                tooltip: `&nbsp;${p.balls}-${p.strikes}, ${p.outsbefore} ${translate('baseball.outs', { count: p.outsbefore })}<br/>
                        ${pitchType(p.pitchtype, true)}${p.velocity ? `, ${translate('format.abbr-mph', { value: number(p.velocity, 1) })}` : ''}<br/>
                        ${translate('common.result')}: ${pitchResult(p.result)}<br/>
                        ${translate('data.swdec-grade')}: ${p.swdecgrade === null || p.swdecgrade === undefined ? '--' : number(p.swdecgrade, 1)}<br/>
                        ${translate('data.miss-prob')}: ${percent(p.pmiss) || '--'}<br/>
                        ${translate('data.con-grade')}: ${p.msgrade === null || p.msgrade === undefined ? '--' : number(p.msgrade, 1)}<br/>
                        ${translate('data.xcq')}: ${p.xcqpred === null || p.xcqpred === undefined ? '--' : number(p.xcqpred, 1)}<br/>
                        ${translate('data.actual-cq')}: ${p.actualcq === null || p.actualcq === undefined ? '--' : number(p.actualcq, 1)}<br/>
                        ${translate('data.cq-grade')}: ${p.cqgrade === null || p.cqgrade === undefined ? '--' : number(p.cqgrade, 1)}<br/>
                        ${translate('data.total-grade')}: ${p.totalgrade === null || p.totalgrade === undefined ? '--' : number(p.totalgrade, 1)}<br/>
                        ${translate('data.swing-probability')}: ${percent(p.pswing)}`,
                url: p.url,
                gameid: p.gameid,
                eventseq: p.eventseq,
                pitchseq: p.pitchseq,
                playid: p.playid,
                pitch: p,
                dataLabels: {
                    enabled: true,
                    x: 1,
                    formatter: () => p.abpitchseq,
                    color: '#000',
                    borderColor: '#FFF',
                },
            }],
        };
    });
}

function mapHittingMonitorScatterPlotSeries(translate, plays, byPitchType = false) {
    if (!plays || !plays.length) return null;

    // hitmon result type enumeration:
    // HARDHIT = 1,
    // HARDOUT = 2,
    // BALL = 3,
    // CALLEDSTRIKE = 4,
    // SWSTRIKE = 5,
    // FOUL = 6,
    // WEAKHIT = 7,
    // WEAKOUT = 8,
    const resultSeries = [{
        data: [],
        name: translate('data.other'),
        index: 0,
        id: 'other',
        color: '#555555',
        marker: { symbol: 'triangle-down', radius: 4, lineColor: '#555555', fillColor: '#555555', lineWidth: 1 },
    }, {
        data: [],
        name: translate('data.hard-hit'),
        index: 1,
        id: 'hit',
        color: '#0000ff',
        marker: { symbol: 'diamond', radius: 4, lineColor: '#0000ff', fillColor: '#ffffff', lineWidth: 2 },
    }, {
        data: [],
        name: translate('data.hard-batted-out'),
        index: 2,
        id: 'out',
        color: '#0000ff',
        marker: { symbol: 'triangle', radius: 5 },
    }, {
        data: [],
        name: translate('baseball.balls_one'),
        index: 3,
        id: 'ball',
        color: '#00ff00',
        marker: { symbol: 'circle', radius: 5 },
    }, {
        data: [],
        name: translate('data.called-strike'),
        index: 4,
        id: 'stk',
        color: '#ff0000',
        marker: { symbol: 'circle', radius: 5 },
    }, {
        data: [],
        name: translate('data.swinging-strike'),
        index: 5,
        id: 'swing',
        color: '#ff0000',
        marker: { symbol: 'diamond', radius: 5 },
    }, {
        data: [],
        name: translate('data.foul'),
        index: 6,
        id: 'foul',
        color: '#ff0000',
        marker: { symbol: 'square', radius: 4, lineColor: '#ff0000', fillColor: '#ffffff', lineWidth: 2 },
    }, {
        data: [],
        name: translate('data.weak-hit'),
        index: 7,
        id: 'wkhit',
        color: '#ffa500',
        marker: { symbol: 'diamond', radius: 4, lineColor: '#ffa500', fillColor: '#ffffff', lineWidth: 2 },
    }, {
        data: [],
        name: translate('data.weak-batted-out'),
        index: 8,
        id: 'wkout',
        color: '#ffa500',
        marker: { symbol: 'triangle', radius: 5 },
    }];

    const pitchTypeSeries = [];

    for (let i = 0; i < plays.length; ++i) {
        if (plays[i].platex && plays[i].platez) {
            const { pitchtype, hitmonresulttype, baseevent, resultid, fxsubtype } = plays[i];
            const point = {
                tooltip:
                    `${''
                    + `${translate('common.date')}: `}${dateTranslate(translate, plays[i].gamedate)}<br />`
                    + `${translate('data.pitch-type')}: ${pitchTypeDisplayName({ pitchtype, fxsubtype })}<br />${
                        plays[i].initspeed54 ? `${translate('format.abbr-mph', { value: number(plays[i].initspeed54) })}<br/>` : ''
                    }${plays[i].istop ? `${translate('baseball.abbr-top')} ` : `${translate('baseball.abbr-bottom')} `}${plays[i].inning}, ${
                        plays[i].outs} ${translate('baseball.outs_one')}, ${
                        plays[i].balls}-${plays[i].strikes
                    }<br />${translate('baseball.pitcher_one')}: ${plays[i].pitcher_usesname} ${plays[i].pitcher_lastname} ${plays[i].islastpitch && (resultid === 14 || resultid === 15)
                        ? `<br />${hitType(plays[i].hittype)} ${baseEvent(baseevent)}
                        ${plays[i].exitvelo ? `, ${translate('data.exit-velo')} ${number(plays[i].exitvelo)}` : ''}`
                        : ''}`,
                x: plays[i].platex,
                y: plays[i].platez,
                gameid: plays[i].gameid,
                eventseq: plays[i].eventseq,
                pitchseq: plays[i].pitchseq,
                id: plays[i].id,
            };
            if (byPitchType) {
                if (pitchTypeSeries.some((s) => s.index === pitchtype)) {
                    pitchTypeSeries.find((s) => s.index === pitchtype).data.push(point);
                } else {
                    const newSeries = {
                        data: [],
                        name: pitchTypeDisplayName({ pitchtype, fxsubtype }),
                        index: pitchtype,
                        id: pitchTypeDisplayName({ pitchtype, fxsubtype }),
                        color: pitchTypeColor(pitchtype),
                        marker: { symbol: 'circle', radius: 4 },
                    };
                    newSeries.data.push(point);
                    pitchTypeSeries.push(newSeries);
                }
            } else if (resultSeries.some((s) => s.index === hitmonresulttype)) {
                resultSeries.find((s) => s.index === hitmonresulttype).data.push(point);
            } else {
                resultSeries[0].data.push(point);
            }
        }
    }

    return byPitchType ? pitchTypeSeries : resultSeries.filter((x) => x.data.length > 0);
}

function damageZonesToSeries(damagezones, isLHP) {
    const damageZoneEllipsePoints = [];
    const ellipseDef = damagezones ? damagezones.filter((x) => x.IsLHP === isLHP) : null;
    if (ellipseDef && ellipseDef.length > 0) {
        const ellipse = ellipseDef[0];
        const n = 40;
        const xradius = ellipse.MajorRadius;
        const yradius = ellipse.MinorRadius;
        const phaseshift = 0;
        for (let i = 0; i < n; i++) {
            const theta = Math.PI * 2 * (i / n);
            const x = ellipse.CenterX - (xradius * Math.cos(theta));
            const y = ellipse.CenterZ - (yradius * Math.sin(theta));
            const xrotate = ((x - ellipse.CenterX) * Math.cos(phaseshift + ellipse.Angle)) - ((y - ellipse.CenterZ) * Math.sin(phaseshift + ellipse.Angle)) + ellipse.CenterX;
            const yrotate = ((x - ellipse.CenterX) * Math.sin(phaseshift + ellipse.Angle)) + ((y - ellipse.CenterZ) * Math.cos(phaseshift + ellipse.Angle)) + ellipse.CenterZ;
            damageZoneEllipsePoints.push({ x: -1 * xrotate, y: yrotate });
        }
    }
    return {
        id: 'ellipse',
        zIndex: 999,
        marker: { fillColor: '#000000', lineColor: '#000000', lineWidth: 2, radius: 1 },
        data: damageZoneEllipsePoints,
    };
}

export {
    mapPPFXReport,
    mapPPFXCountSeries,
    mapHeatMapsFromParams,
    toSwingDecisionSeries,
    mapHittingMonitorScatterPlotSeries,
    damageZonesToSeries,
};
